import { useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthShell } from "@/pages/Login";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Shown when an account was created for someone with a temporary password.
 * Nothing else in the app is reachable until they've chosen their own — the
 * person who set it up shouldn't know their password.
 */
export default function SetPassword() {
  const { user, checkAuth } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.must_change_password) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) {
      toast.error("Use at least 8 characters");
      return;
    }
    if (form.new_password !== form.confirm) {
      toast.error("The two new passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password set");
      await checkAuth();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      heading="Choose your password"
      sub="Your account was set up with a temporary password. Pick your own to carry on."
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="cur">Temporary password</Label>
          <Input
            id="cur"
            type="password"
            required
            autoComplete="current-password"
            value={form.current_password}
            onChange={(e) => setForm({ ...form, current_password: e.target.value })}
          />
          <p className="text-[12px] text-muted2">The one you were given.</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="new">New password</Label>
          <Input
            id="new"
            type="password"
            required
            autoComplete="new-password"
            value={form.new_password}
            onChange={(e) => setForm({ ...form, new_password: e.target.value })}
          />
          <p className="text-[12px] text-muted2">At least 8 characters.</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="conf">Confirm new password</Label>
          <Input
            id="conf"
            type="password"
            required
            autoComplete="new-password"
            value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
          />
        </div>
        <Button type="submit" disabled={busy} className="w-full bg-brand hover:bg-brand-hover h-11">
          {busy ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <KeyRound className="w-4 h-4 mr-2" /> Set password
            </>
          )}
        </Button>
      </form>
    </AuthShell>
  );
}
